import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import MobileBottomNav from "./components/MobileBottomNav";
import Seo from "./components/Seo";
import HomePage from "./pages/HomePage";
import { Toaster } from "sonner";

// Secondary / heavy pages are code-split so the first load ships a smaller bundle.
const CoursesPage = lazy(() => import("./pages/CoursesPage"));
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const ToppersPage = lazy(() => import("./pages/ToppersPage"));
const FacultyPage = lazy(() => import("./pages/FacultyPage"));
const CampusPage = lazy(() => import("./pages/CampusPage"));
const ResidentialPage = lazy(() => import("./pages/ResidentialPage"));
const RtsePage = lazy(() => import("./pages/RtsePage"));
const RtseLoginPage = lazy(() => import("./pages/rtse/RtseLoginPage"));
const RtseResultPage = lazy(() => import("./pages/rtse/RtseResultPage"));
const RtseCertificatePage = lazy(() => import("./pages/rtse/RtseCertificatePage"));
const RtseCounsellorPage = lazy(() => import("./pages/rtse/RtseCounsellorPage"));
const DemoBookingPage = lazy(() => import("./pages/DemoBookingPage"));
const MyProgressPage = lazy(() => import("./pages/MyProgressPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SignupPage = lazy(() => import("./pages/SignupPage"));
const ChangePasswordPage = lazy(() => import("./pages/ChangePasswordPage"));
const StudentPortalPage = lazy(() => import("./pages/StudentPortalPage"));
const MicroTestExamPage = lazy(() => import("./pages/MicroTestExamPage"));
const TestResultPage = lazy(() => import("./pages/TestResultPage"));
const AllenReport = lazy(() => import("./pages/AllenReport"));
const MyTestProgressPage = lazy(() => import("./pages/MyTestProgressPage"));
const ParentViewPage = lazy(() => import("./pages/ParentViewPage"));
const TestLobbyPage = lazy(() => import("./pages/TestLobbyPage"));
const AppTestExamPage = lazy(() => import("./pages/AppTestExamPage"));
const WorkshopPage = lazy(() => import("./pages/WorkshopPage"));
const AdmissionsPage = lazy(() => import("./pages/AdmissionsPage"));
const DownloadsPage = lazy(() => import("./pages/DownloadsPage"));
const SeoLandingPage = lazy(() => import("./pages/SeoLandingPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const AppTestResultPage = lazy(() => import("./pages/AppTestResultPage"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const ControlLoginPage = lazy(() => import("./pages/ControlLoginPage"));
const VerifyPassPage = lazy(() => import("./pages/VerifyPassPage"));
const DmtWorkspace = lazy(() => import("./pages/dmt/DmtWorkspace"));
const DmtExamPage = lazy(() => import("./pages/dmt/student/DmtExamPage"));
const DmtResultPage = lazy(() => import("./pages/dmt/student/DmtResultPage"));
const FacultyPortal = lazy(() => import("./pages/faculty/FacultyPortal"));
const LiveBoardPage = lazy(() => import("./pages/LiveBoardPage"));

function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center" data-testid="page-loader">
      <div className="w-8 h-8 rounded-full border-2 border-slate-300 border-t-[#003399] animate-spin" aria-label="Loading" role="status" />
    </div>
  );
}

const Chromeless = ({ children, dark = false }) => (
  <div className={`min-h-screen font-sans ${dark ? "rk-dark" : ""}`}>
    {children}
    <Toaster richColors position="top-right" theme="dark" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Seo />
        <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Full-screen exam route without navbar/footer */}
          <Route path="/portal/test/:testId/exam" element={
            <div className="min-h-screen rk-dark text-slate-100 font-sans">
              <MicroTestExamPage />
              <Toaster richColors position="top-right" theme="dark" />
            </div>
          } />

          <Route path="/portal/app-test/:testId/exam" element={
            <div className="min-h-screen rk-dark text-slate-100 font-sans">
              <AppTestExamPage />
              <Toaster richColors position="top-right" theme="dark" />
            </div>
          } />

          {/* Product surfaces: dedicated app shell, no marketing chrome */}
          <Route path="/dmt/exam/:testId" element={<><DmtExamPage /><Toaster richColors position="top-center" /></>} />
          <Route path="/dmt/result/:attemptId" element={<><DmtResultPage /><Toaster richColors position="top-center" /></>} />
          <Route path="/dmt/*" element={<><DmtWorkspace /><Toaster richColors position="top-right" /></>} />
          <Route path="/faculty-desk" element={<><FacultyPortal /><Toaster richColors position="top-right" /></>} />
          <Route path="/live-board" element={<><LiveBoardPage /><Toaster richColors position="top-right" theme="dark" /></>} />
          <Route path="/login" element={<Chromeless><LoginPage /></Chromeless>} />
          <Route path="/control" element={<Chromeless><ControlLoginPage /></Chromeless>} />
          <Route path="/portal" element={<Chromeless><StudentPortalPage /></Chromeless>} />
          <Route path="/parent" element={<Chromeless><ParentViewPage /></Chromeless>} />
          <Route path="/admin" element={<Chromeless><AdminDashboardPage /></Chromeless>} />
          <Route path="/portal/test/:testId/lobby" element={<Chromeless dark><TestLobbyPage /></Chromeless>} />
          <Route path="/portal/test/:testId/result" element={<Chromeless dark><TestResultPage /></Chromeless>} />
          <Route path="/portal/test/:testId/report" element={<Chromeless dark><AllenReport /></Chromeless>} />
          <Route path="/portal/my-test-progress" element={<Chromeless><MyTestProgressPage /></Chromeless>} />
          <Route path="/portal/app-test/attempt/:attemptId/result" element={<Chromeless dark><AppTestResultPage /></Chromeless>} />

          {/* All other routes with navbar/footer */}
          <Route path="/*" element={
            <div className="public-shell min-h-screen text-slate-900 flex flex-col font-sans selection:bg-amber-300 selection:text-slate-900">
              <Navbar />
              <main className="flex-1 relative z-10">
                <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/admissions" element={<AdmissionsPage />} />
                  <Route path="/courses" element={<CoursesPage />} />
                  <Route path="/methodology" element={<MethodologyPage />} />
                  <Route path="/toppers" element={<ToppersPage />} />
                  <Route path="/faculty" element={<FacultyPage />} />
                  <Route path="/campus" element={<CampusPage />} />
                  <Route path="/residential" element={<ResidentialPage />} />
                  <Route path="/downloads" element={<DownloadsPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/jee-coaching-in-nagpur" element={<SeoLandingPage />} />
                  <Route path="/neet-coaching-in-nagpur" element={<SeoLandingPage />} />
                  <Route path="/mht-cet-classes-nagpur" element={<SeoLandingPage />} />
                  <Route path="/coaching/:district" element={<SeoLandingPage />} />
                  <Route path="/rtse" element={<RtsePage />} />
                  <Route path="/rtse/login" element={<RtseLoginPage />} />
                  <Route path="/rtse/counsellor" element={<RtseCounsellorPage />} />
                  <Route path="/rtse/result/:attemptId" element={<RtseResultPage />} />
                  <Route path="/rtse/certificate/:token" element={<RtseCertificatePage />} />
                  <Route path="/demo" element={<DemoBookingPage />} />
                  <Route path="/workshop" element={<WorkshopPage />} />
                  <Route path="/my-progress" element={<MyProgressPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/change-password" element={<ChangePasswordPage />} />
                  <Route path="/verify" element={<VerifyPassPage />} />
                  <Route path="/verify/:passId" element={<VerifyPassPage />} />
                </Routes>
                </Suspense>
              </main>
              <Footer />
              <WhatsAppFloat />
              <MobileBottomNav />
              <Toaster richColors position="top-right" theme="dark" />
            </div>
          } />
        </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
